/**
 * Tile quantity calculator v2 — synchronises SQM and Tiles inputs.
 * sqm_per_tile = (tile_width_mm / 1000) * (tile_height_mm / 1000)
 *
 * PRICING MODEL:
 * - The Shopify cart quantity is Math.round(sqm), NOT the tile count.
 *   e.g. 10.08 -> 10, 10.49 -> 10, 10.50 -> 11, 10.53 -> 11, 10.99 -> 11
 * - "Tiles Required" is still calculated internally and passed through as a
 *   line item property only — it is never used as the cart quantity.
 * - Volume discounts are read dynamically from the existing HulkApps
 *   quantity-discount table on the page (NOT hardcoded), and are applied
 *   against the rounded SQM quantity instead of the tile quantity.
 * - Total price = Rounded SQM Quantity * Applicable Discounted SQM Price.
 * - Because the cart quantity (Math.round(sqm)) and the corresponding
 *   discounted unit price are both computed here before the cart update
 *   fires, the same numbers Shopify uses to price the cart drawer and
 *   checkout line item are the ones shown on the product page.
 * - Minimum order validation is still based on the entered SQM value.
 */
(function () {
  const round = (value, decimals = 2) => {
    const factor = 10 ** decimals;
    return Math.round(value * factor) / factor;
  };

  const formatTotal = (cents) => {
    if (typeof window.formatMoney === 'function' && window.MinimogSettings?.money_format) {
      return window.formatMoney(cents, window.MinimogSettings.money_format);
    }
    return `£${(cents / 100).toFixed(2)}`;
  };

  const parseNumber = (value) => {
    if (value === null || value === undefined || String(value).trim() === '') {
      return null;
    }
    const num = Number(String(value).replace(',', '.'));
    return Number.isFinite(num) ? num : null;
  };

  class TileCalculatorV2 extends HTMLElement {
    connectedCallback() {
      this.tileWidthMm = Number(this.dataset.tileWidthMm) || 1000;
      this.tileHeightMm = Number(this.dataset.tileHeightMm) || 1000;

      this.variants = [];
      const jsonEl = this.querySelector('.calculator-variant-data');
      if (jsonEl) {
        try {
          this.variants = JSON.parse(jsonEl.textContent);
        } catch (e) {
          console.error(e);
        }
      }
      let w = Number(this.dataset.tileWidthMm) || 1000;
      let h = Number(this.dataset.tileHeightMm) || 1000;
      let foundDimensions = false;

      // Try to find the checked variant option in the DOM safely
      const productBlock = this.closest('.main-product-blocks') || document;
      const checkedInputs = productBlock.querySelectorAll('.variant-picker__option input:checked');

      checkedInputs.forEach(input => {
        const optionVal = input.value || "";
        const m = optionVal.match(/(\d+)\s*[xX*]\s*(\d+)/);
        if (m) {
          w = parseFloat(m[1]);
          h = parseFloat(m[2]);
          foundDimensions = true;
        }
      });

      if (!foundDimensions) {
        // Fallback to dataset
        const initialTitle = this.dataset.initialVariantTitle || "";
        const initMatch = initialTitle.match(/(\d+)\s*[xX*]\s*(\d+)/);
        if (initMatch) {
          w = parseFloat(initMatch[1]);
          h = parseFloat(initMatch[2]);
        }
      }

      if (w > 0 && h > 0) {
        this.tileWidthMm = w;
        this.tileHeightMm = h;
      }
      this.unitPrice = Number(this.dataset.unitPrice) || 0; // price per SQM, in cents
      this.productId = this.dataset.productId;

      this.sqmInput = this.querySelector('[data-calc-sqm]');
      this.tilesInput = this.querySelector('[data-calc-tiles]');
      this.quantityInput = this.querySelector('[data-calc-quantity]');
      this.totalEl = this.querySelector('[data-calc-total]');
      this.unitPriceEl = this.querySelector('[data-calc-unit-price]');
      this.propertyCoverageInput = this.querySelector('[data-calc-property-coverage]');
      this.propertyTilesInput = this.querySelector('[data-calc-property-tiles]'); // "Tiles Required" property

      const tileWidthM = this.tileWidthMm / 1000;
      const tileHeightM = this.tileHeightMm / 1000;
      this.sqmPerTile = tileWidthM * tileHeightM;

      // this.minSqm is the effective minimum order size, expressed in SQM.
      // It combines a directly configured minimum SQM with a legacy
      // "minimum tile count" setting (converted to SQM using the current
      // tile size), so existing theme settings keep working.
      this.updateMinSqm();
      this.updating = false;

      if (!this.sqmPerTile || !this.sqmInput || !this.tilesInput || !this.quantityInput) {
        return;
      }

      const initialSqm = round(Math.max(this.minSqm, this.sqmPerTile));
      this.sqmInput.value = initialSqm.toFixed(2);
      this.tilesInput.value = this.tilesFromSqm(initialSqm);
      this.savedVariantSqms = {};
      this.currentVariantKey = "default";
      this.savedVariantSqms[this.currentVariantKey] = initialSqm;
      this.commitOutputs(false);

      this.bindEvents();
    }

    // Combines dataset.minSqm (direct SQM minimum) with dataset.minQuantity
    // (legacy tile-count minimum) into a single effective minimum SQM value.
    updateMinSqm() {
      const configuredMinTiles = Number(this.dataset.minQuantity) || 1;
      const configuredMinSqm = Number(this.dataset.minSqm) || 0;
      const tileBasedMinSqm = this.sqmPerTile > 0 ? configuredMinTiles * this.sqmPerTile : 0;
      this.minSqm = Math.max(configuredMinSqm, tileBasedMinSqm);
    }

    bindEvents() {
      this.sqmInput.addEventListener('input', () => this.handleSqmInput());
      this.sqmInput.addEventListener('blur', () => this.handleSqmBlur());
      this.tilesInput.addEventListener('input', () => this.handleTilesInput());
      this.tilesInput.addEventListener('blur', () => this.handleTilesBlur());

      const formId = this.quantityInput.getAttribute('form');
      const form = formId ? document.getElementById(formId) : this.closest('form');
      if (form) {
        form.addEventListener('submit', (e) => this.handleFormSubmit(e));
      }

      const atcBtn = this.querySelector('.add-to-cart');
      if (atcBtn) {
        atcBtn.addEventListener('click', (e) => this.handleFormSubmit(e), true);
      }

      const productBlock = this.closest('.main-product-blocks') || document;
      productBlock.addEventListener('change', (e) => {
        if (e.target && e.target.closest('.variant-picker__option')) {
           this.handleVariantChange();
        }
      });

      if (this.productId && window.MinimogEvents) {
        window.MinimogEvents.subscribe(`${this.productId}__VARIANT_CHANGE`, (variant) => {
           // We just trigger our own logic so we don't rely on the variant payload
           this.handleVariantChange();
        });
      }

      // The HulkApps discount table can render/update asynchronously after
      // this component initialises. Watch it so the displayed price/total
      // stay in sync with whatever tiers are currently configured in Hulk.
      const hook = document.querySelector('.quantity-discount-hook');
      if (hook) {
        const observer = new MutationObserver(() => {
          if (this.hasQuantity()) {
            this.previewOutputs();
          }
        });
        observer.observe(hook, { childList: true, subtree: true });
      }
    }

    handleVariantChange() {
      if (!this.variants || this.variants.length === 0) return;

      const productBlock = this.closest('.main-product-blocks') || document;
      const checkedInputs = Array.from(productBlock.querySelectorAll('.variant-picker__option input:checked'));
      const selectedOptions = checkedInputs.map(input => input.value);

      // Find matching variant
      const matchedVariant = this.variants.find(variant => {
        if (!variant.options || variant.options.length !== selectedOptions.length) return false;
        return variant.options.every((opt, index) => opt === selectedOptions[index]);
      });

      if (matchedVariant) {
        this.unitPrice = matchedVariant.price; // price per SQM, in cents

        let optionsText = matchedVariant.title || matchedVariant.options.join(" ");
        const match = optionsText.match(/(\d+)\s*[xX*]\s*(\d+)/);
        if (match) {
          const w = parseFloat(match[1]);
          const h = parseFloat(match[2]);
          if (w > 0 && h > 0) {
            this.sqmPerTile = (w / 1000) * (h / 1000);
          }
        }
        this.updateMinSqm();

        this.currentVariantKey = matchedVariant.id || optionsText;

        let sqmToApply;
        if (this.savedVariantSqms[this.currentVariantKey]) {
          // If the user previously typed a custom amount FOR THIS EXACT VARIANT, restore it!
          sqmToApply = this.savedVariantSqms[this.currentVariantKey];
        } else {
          // Otherwise, just reset to the baseline minimum of the new variant
          const newMinAllowedSqm = round(this.minSqm);
          sqmToApply = newMinAllowedSqm;
          this.savedVariantSqms[this.currentVariantKey] = newMinAllowedSqm;
        }

        this.applyFromSqm(sqmToApply, true, true);
      }
    }

    // Reads the current volume-discount tiers dynamically from the
    // HulkApps quantity-discount table rendered on the page, and applies
    // whichever tier matches the rounded SQM quantity (sqmQty). Nothing
    // here is hardcoded — if the tiers change in the Hulk app, this will
    // automatically pick up the new values next time the page renders them.
    getApplicableUnitPrice(sqmQty) {
      let rows = [];
      const productBlock = this.closest('.main-product-blocks') || document;
      const hook = productBlock.querySelector('.quantity-discount-hook');
      if (hook) {
        rows = Array.from(hook.querySelectorAll('tr'));
      }

      // Fallback: If the hook is empty or missing, search all tables on the page
      if (!rows.length) {
        const tables = document.querySelectorAll('table, .hulkapps-volumes');
        for (const t of tables) {
          if (t.textContent.includes('% Off') || t.textContent.includes('Discount')) {
            rows = Array.from(t.querySelectorAll('tr'));
            if (!rows.length) {
              // Sometimes HulkApps uses divs instead of tr
              rows = Array.from(t.querySelectorAll('.hulkapps-volume-row, [class*="row"]'));
            }
            break;
          }
        }
      }

      if (!rows.length) return this.unitPrice;

      let applicableUnitPrice = this.unitPrice;
      let applicableQty = 0;

      rows.forEach(row => {
        const cells = row.cells || row.querySelectorAll('td, div');
        if (!cells || cells.length < 2) return;

        const qtyText = cells[0]?.textContent || '';
        const qtyMatch = qtyText.match(/(\d+)/);
        if (qtyMatch) {
          const tierQty = parseInt(qtyMatch[1], 10);
          // Tiers are matched against the rounded SQM quantity, not tiles.
          if (sqmQty >= tierQty && tierQty >= applicableQty) {
            applicableQty = tierQty;

            const discountText = cells[1]?.textContent || '';
            const percentMatch = discountText.match(/(\d+)%\s*Off/i);

            if (percentMatch) {
              const discountPercent = parseFloat(percentMatch[1]);
              const discountAmountCents = Math.floor(this.unitPrice * (discountPercent / 100));
              applicableUnitPrice = this.unitPrice - discountAmountCents;
            } else {
              const priceMatch = discountText.match(/£([\d.]+)/);
              if (priceMatch) {
                applicableUnitPrice = Math.round(parseFloat(priceMatch[1]) * 100);
              }
            }
          }
        }
      });
      return applicableUnitPrice;
    }

    updateUnitPriceDisplay(currentSqmPrice) {
      if (this.unitPriceEl) {
        const formatted = typeof window.formatMoney === 'function'
          ? window.formatMoney(currentSqmPrice, window.MinimogSettings?.money_format)
          : `£${(currentSqmPrice / 100).toFixed(2)}`;
        this.unitPriceEl.innerHTML = `${formatted}<span class="tile-calculator-v2__unit-price-suffix"> /sqm</span>`;
      }
    }

    // Tiles required to cover a given SQM amount. Purely informational now —
    // never used as the Shopify cart quantity.
    tilesFromSqm(sqm) {
      // Subtract a tiny epsilon to fix floating point math errors where 8.64 / 0.72 becomes 12.000000002 which ceiling'd to 13.
      return Math.max(1, Math.ceil((sqm / this.sqmPerTile) - 0.0001));
    }

    sqmFromTiles(tiles) {
      const count = Math.max(1, Math.ceil(tiles));
      return round(count * this.sqmPerTile);
    }

    // Shopify cart quantity is Math.round(sqm).
    // e.g. 10.08 -> 10, 10.49 -> 10, 10.50 -> 11, 10.53 -> 11, 10.99 -> 11
    cartQtyFromSqm(sqm) {
      return Math.max(0, Math.round(sqm));
    }

    hasQuantity() {
      const sqm = parseNumber(this.sqmInput.value);
      return sqm !== null && sqm > 0;
    }

    clearOutputs() {
      if (this.totalEl) {
        this.totalEl.textContent = '';
      }
      this.quantityInput.value = '';
    }

    clearFields() {
      this.updating = true;
      this.sqmInput.value = '';
      this.tilesInput.value = '';
      this.updating = false;
      this.clearOutputs();
    }

    handleSqmInput() {
      if (this.updating) return;

      if (this.sqmInput.value.trim() === '') {
        this.updating = true;
        this.tilesInput.value = '';
        this.updating = false;
        this.clearOutputs();
        return;
      }

      const sqm = parseNumber(this.sqmInput.value);
      if (sqm === null || sqm <= 0) {
        this.clearOutputs();
        return;
      }

      this.updating = true;
      this.tilesInput.value = this.tilesFromSqm(sqm);
      this.updating = false;

      this.previewOutputs();
    }

    handleSqmBlur() {
      if (this.sqmInput.value.trim() === '') {
        this.clearFields();
        return;
      }

      const sqm = parseNumber(this.sqmInput.value);
      if (sqm === null || sqm <= 0) {
        this.clearFields();
        return;
      }

      // Minimum order validation is based on the entered SQM value.
      const finalSqm = round(Math.max(sqm, this.minSqm));
      if (this.currentVariantKey) {
        this.savedVariantSqms[this.currentVariantKey] = finalSqm;
      }
      this.applyFromSqm(finalSqm, true, true);
    }

    handleTilesInput() {
      if (this.updating) return;

      if (this.tilesInput.value.trim() === '') {
        this.updating = true;
        this.sqmInput.value = '';
        this.updating = false;
        this.clearOutputs();
        return;
      }

      const tiles = parseNumber(this.tilesInput.value);
      if (tiles === null || tiles <= 0) {
        this.clearOutputs();
        return;
      }

      const sqm = this.sqmFromTiles(tiles);

      this.updating = true;
      this.sqmInput.value = sqm.toFixed(2);
      this.updating = false;

      this.previewOutputs();
    }

    handleTilesBlur() {
      if (this.tilesInput.value.trim() === '') {
        this.clearFields();
        return;
      }

      let tiles = parseNumber(this.tilesInput.value);
      if (tiles === null) {
        this.clearFields();
        return;
      }

      tiles = Math.max(1, Math.ceil(tiles));
      let sqm = this.sqmFromTiles(tiles);

      // Minimum order validation is based on the resulting SQM value, not tiles.
      if (sqm < this.minSqm) {
        sqm = round(this.minSqm);
        tiles = this.tilesFromSqm(sqm);
      }

      if (this.currentVariantKey) {
        this.savedVariantSqms[this.currentVariantKey] = sqm;
      }
      this.applyFromTiles(tiles, sqm, true, true);
    }

    applyFromSqm(sqm, emitEvent, format = false) {
      const tiles = this.tilesFromSqm(sqm);

      this.updating = true;
      this.sqmInput.value = format ? sqm.toFixed(2) : sqm;
      this.tilesInput.value = tiles;
      this.updating = false;

      this.commitOutputs(emitEvent);
    }

    applyFromTiles(tiles, sqm, emitEvent, format = false) {
      this.updating = true;
      this.tilesInput.value = tiles;
      this.sqmInput.value = format ? sqm.toFixed(2) : sqm;
      this.updating = false;

      this.commitOutputs(emitEvent);
    }

    getBaseSqm() {
      const sqm = parseNumber(this.sqmInput.value);
      if (sqm !== null && sqm > 0) {
        return sqm;
      }
      return null;
    }

    previewOutputs() {
      const baseSqm = this.getBaseSqm();
      if (baseSqm === null) {
        this.clearOutputs();
        this.updateUnitPriceDisplay(this.unitPrice);
        return;
      }

      const sqmQty = this.cartQtyFromSqm(baseSqm);
      const currentUnitPrice = this.getApplicableUnitPrice(sqmQty);
      const totalCents = Math.round(sqmQty * currentUnitPrice);

      if (this.totalEl) {
        this.totalEl.textContent = formatTotal(totalCents);
      }
      this.updateUnitPriceDisplay(currentUnitPrice);
    }

    commitOutputs(emitEvent = true) {
      const baseSqm = this.getBaseSqm();
      if (baseSqm === null) {
        this.clearOutputs();
        this.updateUnitPriceDisplay(this.unitPrice);
        return;
      }

      const sqmQty = this.cartQtyFromSqm(baseSqm); // Math.round(sqm) — this is the Shopify cart quantity
      const tilesRequired = this.tilesFromSqm(baseSqm); // informational only, never the cart quantity
      const currentSqmPrice = this.getApplicableUnitPrice(sqmQty);
      const totalCents = Math.round(sqmQty * currentSqmPrice);

      // Shopify cart quantity = Math.round(sqm), NOT tile count.
      this.quantityInput.value = sqmQty;
      this.quantityInput.min = Math.max(1, Math.round(this.minSqm));

      if (this.propertyCoverageInput) {
        this.propertyCoverageInput.value = baseSqm.toFixed(2);
      }
      if (this.propertyTilesInput) {
        // Line item property: "Tiles Required" — informational only.
        this.propertyTilesInput.value = tilesRequired;
      }

      if (this.totalEl) {
        this.totalEl.textContent = formatTotal(totalCents);
      }
      this.updateUnitPriceDisplay(currentSqmPrice);

      if (emitEvent && this.productId && window.MinimogEvents) {
        window.MinimogEvents.emit(`${this.productId}__QUANTITY_CHANGE`, sqmQty);
      }

      this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    handleFormSubmit(e) {
      if (!this.hasQuantity()) {
        e.preventDefault();
        e.stopPropagation();
        this.sqmInput.focus();
        return;
      }

      const baseSqm = this.getBaseSqm();
      if (baseSqm !== null && baseSqm < this.minSqm) {
        // Enforce minimum order validation (based on SQM) before submit.
        const finalSqm = round(this.minSqm);
        if (this.currentVariantKey) {
          this.savedVariantSqms[this.currentVariantKey] = finalSqm;
        }
        this.applyFromSqm(finalSqm, true, true);
      }

      this.commitOutputs(true);
    }
  }

  if (!customElements.get('tile-calculator-v2')) {
    customElements.define('tile-calculator-v2', TileCalculatorV2);
  }
})();